import { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { PaymentTrustBadges } from "@/components/PaymentTrustBadges";
import { Button } from "@/components/ui/button";
import { PreReserveDialog } from "@/components/pdp/PreReserveDialog";
import { ReviewsSection } from "@/components/reviews/ReviewsSection";
import { productIdForHandle } from "@/lib/productIds";
import { SOLD_OUT, PREPAID_ONLY, isComingSoon } from "@/lib/storeStatus";
import { NO_COST_EMI_MIN_INR } from "@/lib/checkout-pricing";
import type { BundlePageData } from "@/lib/bundlePage";
import type { ShopifyProduct } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { buildOvaBundleCartLines } from "./ovaBundleCommerce";
import {
  contributionsByHandle, getBundleFaqs, getBundleRoutine, getBundleSupply, getBundleTimeline,
  ingredientScenes, ingredientsByHandle, ovaBundleContent, type OvaBundleKind,
} from "./ovaBundleContent";
import { useOvaBundleMotion } from "./useOvaBundleMotion";
import { BundleEcosystemSection } from "./BundleEcosystemSection";
import { bundleBySlug } from "@/lib/ovaEcosystem";
import "./ova-bundles.css";


const money = (value: number) => `₹${Math.round(value).toLocaleString("en-IN")}`;
function Lines({ lines }: { lines: string[] }) {
  return <>{lines.map((line, index) => <Fragment key={line}>{index > 0 && <br />}{line}</Fragment>)}</>;
}

export function OvaBundleOfferPage({ data, kind, heroFor }: {
  data: BundlePageData;
  kind: OvaBundleKind;
  heroFor: (product: ShopifyProduct["node"]) => string | null;
}) {
  const copy = ovaBundleContent[kind];
  const supply = getBundleSupply(kind, data.qty);
  const routine = getBundleRoutine(kind, data.qty);
  const addItems = useCartStore((state) => state.addItems);
  const cartLoading = useCartStore((state) => state.isLoading);
  const [added, setAdded] = useState(false);
  const [error, setError] = useState("");
  const pending = useRef(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const rootRef = useOvaBundleMotion(`${data.rule.slug}:${kind}:${data.qty}`);
  const reserveOva = isComingSoon("ova-balance");
  const otherComingSoon = data.items.some((item) => item.handle !== "ova-balance" && isComingSoon(item.handle));
  const cartLines = buildOvaBundleCartLines(data);
  const cannotBuy = SOLD_OUT || otherComingSoon || !cartLines;
  useEffect(() => () => { clearTimeout(resetTimer.current); }, []);
  useEffect(() => { setAdded(false); setError(""); }, [data.rule.slug, kind, data.qty]);

  async function handleAdd() {
    if (pending.current || cartLoading || reserveOva || cannotBuy) return;
    const lines = buildOvaBundleCartLines(data);
    if (!lines) return;
    pending.current = true;
    setError("");
    try {
      const result = await addItems(lines);
      if (!result) throw new Error("Unable to add bundle");
      setAdded(true);
      (window as Window & { __openCart?: () => void }).__openCart?.();
      clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setAdded(false), 2200);
    } catch {
      setError("We could not add the bundle. Please try again.");
    } finally {
      pending.current = false;
    }
  }

  function purchaseAction(placement: "hero" | "offer") {
    if (reserveOva) {
      return <PreReserveDialog
        productHandle="ova-balance" productName="Ova Balance" source={`bundle-${placement}-${data.rule.slug}`.slice(0, 64)}
        trigger={<Button type="button" className="tw-main-button" data-testid={`bundle-prebook-${placement}`}>Pre-book Ova Balance</Button>}
      />;
    }
    return <Button type="button" className="tw-main-button" data-testid={placement === "hero" ? "bundle-add-to-cart" : "bundle-add-to-cart-bottom"}
      disabled={cartLoading || cannotBuy} onClick={() => void handleAdd()}>
      {SOLD_OUT ? "Sold out" : cannotBuy ? "Temporarily unavailable" : cartLoading ? "Adding…" : added ? "Added to cart" : "Add bundle to cart"}
    </Button>;
  }
  function OfferPrice() {
    return <div className="tw-hero-offer"><div>
      <strong>{money(data.total)}</strong>{data.savings > 0 && <del>{money(data.subtotal)}</del>}
      {data.savings > 0 && <span>Save {money(data.savings)} · {data.percent}% compared with buying separately</span>}
    </div></div>;
  }
  const availabilityFaq = {
    question: "Where is it available?",
    answer: reserveOva
      ? "Available on truwe.in. You can pre-book Ova Balance today, free of charge. No payment is collected during pre-booking."
      : "Available on truwe.in. Choose your bundle here and add it to your cart. Availability is confirmed at checkout.",
  };

  const entity = bundleBySlug(data.rule.slug);

  return <main ref={rootRef} className="ova-bundle-page" data-bundle-kind={kind}>
    <nav className="obp-breadcrumb" aria-label="Breadcrumb">
      <Link to="/">Home</Link><span>/</span>
      <Link to="/shop">Shop</Link><span>/</span>
      <Link to="/collections/$slug" params={{ slug: "pcos-nutrition" }}>PCOS Nutrition</Link><span>/</span>
      <span>{data.rule.title}</span>
    </nav>
    <section className="tw-hero" aria-labelledby="ova-bundle-title">
      <div className="tw-hero-copy">
        <p className="tw-eyebrow">{data.rule.title} · {copy.handles.map((handle) => handle === "ova-balance" ? "Ova" : handle === "mag-absorb-pro" ? "Mag" : "Liver").join(" + ")}</p>
        <h1 id="ova-bundle-title">{data.rule.title}</h1>
        <p className="tw-subtitle"><strong>{entity?.supportLine ?? copy.handles.join(" + ")}</strong></p>
        <p className="tw-subtitle">{copy.intro}</p>
        <div className="tw-pill-row">{copy.pills.map((pill) => <span className="tw-pill" key={pill}>{pill}</span>)}</div>
        <OfferPrice />
        {purchaseAction("hero")}
        <a className="obp-story-link" href="#ova-ingredient-story">See what each adds ↓</a>
        <p className="tw-small">{reserveOva ? "Free to pre-book · No payment now. This reserves Ova Balance, not all products in the bundle." : copy.heroNote}</p>
      </div>
      <figure className="tw-hero-photo"><img src={copy.heroImage.src} alt={copy.heroImage.alt} style={{ objectPosition: copy.heroImage.position }} width={1024} height={1024} fetchPriority="high" decoding="async" /><figcaption className="tw-photo-caption">A routine with room for real life.</figcaption></figure>
    </section>
    <div className="tw-strip">{copy.strip}</div>
    {entity && <BundleEcosystemSection bundle={entity} />}

    <section className="tw-section" aria-labelledby="ova-contributions">
      <div className="tw-section-head"><div><p className="tw-eyebrow">Why these belong together</p><h2 id="ova-contributions">Every formula<br />has a job.</h2></div><p>{copy.whyIntro}</p></div>
      <div className="tw-products" style={{ gridTemplateColumns: `repeat(${copy.handles.length}, minmax(0, 1fr))` }}>
        {copy.handles.map((handle) => {
          const product = data.items.find((item) => item.handle === handle);
          if (!product) return null;
          const contribution = contributionsByHandle[handle];
          const src = heroFor(product);
          return <article className="tw-product" key={handle}>
            <Link to="/products/$handle" params={{ handle }} className="tw-product-photo" aria-label={`View ${product.title}`}>{src && <img src={src} alt={product.title} width={400} height={400} loading="lazy" decoding="async" />}</Link>
            <div><div className="tw-role">{contribution.role}</div><h3>{product.title}</h3><p>{contribution.body}</p><p className="tw-small">{contribution.unitsPerPack * data.qty} {contribution.unit} · {data.qty} {data.qty === 1 ? "pack" : "packs"}</p></div>
          </article>;
        })}
      </div>
      <div className="tw-together"><span className="tw-eyebrow">The combined value</span><p>{copy.together}</p></div>
    </section>
    <section className="tw-section tw-story" id="ova-ingredient-story" aria-labelledby="ova-story-title">
      <div className="tw-section-head"><div><p className="tw-eyebrow">Inside your daily routine</p><h2 id="ova-story-title">A little ritual.<br />A clear purpose.</h2></div><p>Get to know the ingredients, one thoughtful layer at a time.</p></div>
      <div className="tw-chapters">{copy.sceneIds.map((id, index) => {
        const scene = ingredientScenes[id];
        return <article className="tw-chapter" key={id} style={{ zIndex: index + 1 }}>
          <div className="tw-cine-card"><figure className="tw-cine-image"><img src={scene.image.src} alt={scene.image.alt} style={{ objectPosition: scene.image.position }} width={600} height={900} loading="lazy" decoding="async" /><div className="tw-cine-chip"><span>{scene.dose.split(" · ")[0]}</span><span>{String(index + 1).padStart(2, "0")} / {String(copy.sceneIds.length).padStart(2, "0")}</span></div></figure>
            <div className="tw-cine-copy"><div className="tw-cine-stat">{scene.main}</div><div className="tw-cine-sub">{scene.sub}</div><h3>{scene.title}</h3><p>{scene.body}</p><div className="tw-dose-tag">{scene.dose}</div></div>
          </div>
        </article>;
      })}</div>
      <p className="tw-art-note">Ingredient-inspired imagery. Daily-life illustrations.</p>
    </section>
    {kind !== "liver" && <section className="tw-section tw-mag" aria-labelledby="ova-mag-direction">
      <div><p className="tw-eyebrow">Your Ova + Mag direction</p><h2 id="ova-mag-direction">With Ova,<br />just one Mag tablet.</h2><p>Ova already contributes 100 mg of elemental magnesium. One Mag Absorb Pro tablet adds 220 mg, bringing this paired daily routine to 320 mg.</p><p>One Ova sachet with a meal. One Mag tablet in the evening. A clear routine, with the overlap already counted.</p></div>
      <div><div className="tw-math"><div><strong>100<small> mg</small></strong><span>1 Ova sachet</span></div><b aria-hidden="true">+</b><div><strong>220<small> mg</small></strong><span>1 Mag tablet</span></div></div><div className="tw-total"><strong>320 mg</strong><br />elemental magnesium per day from the pair</div><div className="tw-mineral-bar" role="img" aria-label="100 mg from Ova plus 220 mg from Mag equals 320 mg"><span /><span /></div><p className="tw-small">Bundle-specific direction. Count any other magnesium supplements when reviewing your complete routine.</p></div>
    </section>}
    <section className="tw-section" aria-labelledby="ova-timeline-title">
      <div className="tw-section-head"><div><p className="tw-eyebrow">Your 24-hour routine</p><h2 id="ova-timeline-title">Spaced across<br />the day.</h2></div><p>Each formula has its own slot, so the doses stay apart and any overlapping minerals are already counted.</p></div>
      <ol className="tw-timeline">
        {getBundleTimeline(kind).map((step) => <li className="tw-timeline-step" key={step.slot}>
          <span className="tw-timeline-slot">{step.slot}</span>
          <div><span className="tw-timeline-time">{step.time}</span><strong>{step.product}</strong><p>{step.detail}</p></div>
        </li>)}
      </ol>
    </section>
    <section className="tw-lifestyle" aria-labelledby="ova-routine-title">
      <figure><img src={copy.routineImage.src} alt={copy.routineImage.alt} style={{ objectPosition: copy.routineImage.position }} width={1024} height={1024} loading="lazy" decoding="async" /><figcaption className="tw-photo-caption">Illustrative lifestyle scene</figcaption></figure>
      <div className="tw-lifestyle-copy"><p className="tw-eyebrow">Simple enough to keep doing</p><h2 id="ova-routine-title"><Lines lines={copy.routineTitleLines} /></h2><div>{routine.map((row) => <div className="tw-routine-row" key={row.title}><strong>{row.title}</strong><p>{row.body}</p></div>)}</div></div>
    </section>
    <section className="tw-section" aria-labelledby="ova-ingredients-title">
      <div className="tw-section-head"><div><p className="tw-eyebrow">Know what you are choosing</p><h2 id="ova-ingredients-title">A purpose for<br />every ingredient.</h2></div><p>Explore the formula details, then choose the level of support that fits your priorities.</p></div>
      {copy.handles.map((handle) => { const group = ingredientsByHandle[handle]; return <details className="tw-details" key={handle}><summary>{group.title}</summary><div className="tw-ingredients">{group.items.map((ingredient) => <div className="tw-ingredient" key={ingredient.name}><strong>{ingredient.name}</strong><small>{ingredient.dose}</small><p>{ingredient.body}</p></div>)}</div></details>; })}
    </section>
    <section className="tw-section tw-order" aria-labelledby="ova-offer-title">
      <div><p className="tw-eyebrow">{data.rule.title}</p><h2 id="ova-offer-title"><Lines lines={copy.closeLines} /></h2>{supply.contents.map((line) => <p key={line}>{line}</p>)}<p className="tw-small">{supply.duration}</p></div>
      <div><OfferPrice />{purchaseAction("offer")}<p className="tw-small">{reserveOva ? "Free Ova Balance pre-booking is open on truwe.in. No payment now; the displayed bundle price is not collected by this reservation form." : PREPAID_ONLY ? "The bundle discount applies automatically in your cart. Prepaid orders only." : "The bundle discount applies automatically in your cart."}</p>
        {!reserveOva && <>{data.total >= NO_COST_EMI_MIN_INR && <p className="tw-small">No-cost EMI available on this bundle</p>}<PaymentTrustBadges /></>}
      </div>
    </section>
    <div className="obp-feedback" role="status" aria-live="polite">{error || (added ? "Bundle added to your cart." : "")}</div>
    <section className="tw-section" aria-labelledby="ova-faq-title"><div className="tw-section-head"><div><p className="tw-eyebrow">Before you choose</p><h2 id="ova-faq-title">The practical questions.</h2></div></div>
      {[availabilityFaq, ...getBundleFaqs(kind, data.qty)].map((faq) => <details className="tw-details" key={faq.question}><summary>{faq.question}</summary><p>{faq.answer}</p></details>)}
    </section>
    {data.items.some((item) => productIdForHandle(item.handle)) && <section className="tw-section" aria-labelledby="ova-reviews-title"><h2 id="ova-reviews-title">What customers say</h2>{data.items.map((item) => { const id = productIdForHandle(item.handle); return id ? <div className="obp-product-reviews" key={item.id}><h3>{item.title}</h3><ReviewsSection productId={id} /></div> : null; })}</section>}
  </main>;
}
